import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { useState } from 'react';
import FlowCategoriesList from '@Components/FlowCategories/List';
import FlowCategoryForm from '@Components/FlowCategories/Form';
import Breadcrumbs from '@Components/Breadcrumbs';
import Heading from '@Components/Heading';
import type { FlowCategory } from '@Types/flowCategory';

const CREATE_FORM_ID = 'create-category-form';
const EDIT_FORM_ID = 'edit-category-form';

export default function FlowCategories() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editCategory, setEditCategory] = useState<FlowCategory | null>(null);

  const handleEditClick = (row: { original: FlowCategory }) => {
    setEditCategory(row.original);
    setEditOpen(true);
  };

  const handleCreateClick = () => {
    setCreateOpen(true);
  };

  return (
    <div>
      <Breadcrumbs
        navList={[
          { name: 'Flows', href: '/flows' },
          { name: 'Categories', href: '/flows/categories' },
        ]}
      />
      <div className="mt-8 mb-4 flex items-center justify-between">
        <div>
          <Heading>Flow Categories</Heading>
        </div>

        {/* Create Sheet */}
        <Sheet open={createOpen} onOpenChange={setCreateOpen}>
          <SheetContent side="right" className="w-full sm:max-w-xl">
            <SheetHeader>
              <SheetTitle>Create category</SheetTitle>
              <SheetDescription>
                Add a new category with a name, color and icon.
              </SheetDescription>
            </SheetHeader>

            <SheetBody className="grow">
              <FlowCategoryForm
                onSuccess={() => setCreateOpen(false)}
                submitLabel="Create"
                formId={CREATE_FORM_ID}
                hideInternalActions
              />
            </SheetBody>

            <SheetFooter className="mt-4">
              <Button
                type="submit"
                form={CREATE_FORM_ID}
                variant="primary"
                className="grow"
              >
                Create
              </Button>
              <SheetClose asChild>
                <Button variant="secondary" type="button" className="grow">
                  Cancel
                </Button>
              </SheetClose>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>

      <FlowCategoriesList
        onEditClick={handleEditClick}
        onCreateClick={handleCreateClick}
      />

      {/* Edit Sheet */}
      <Sheet
        open={editOpen}
        onOpenChange={(isOpen) => {
          setEditOpen(isOpen);
          if (!isOpen) setEditCategory(null);
        }}
      >
        <SheetContent side="right" className="w-full sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Edit category</SheetTitle>
            <SheetDescription>
              Update the category name, color and icon.
            </SheetDescription>
          </SheetHeader>

          <SheetBody className="grow">
            {editCategory && (
              <FlowCategoryForm
                key={editCategory.id}
                editId={editCategory.id}
                defaultValues={{
                  name: editCategory.name ?? '',
                  color: editCategory.color ?? '',
                  icon: editCategory.icon ?? '',
                }}
                onSuccess={() => {
                  setEditOpen(false);
                  setEditCategory(null);
                }}
                submitLabel="Update"
                formId={EDIT_FORM_ID}
                hideInternalActions
              />
            )}
          </SheetBody>

          <SheetFooter className="mt-4">
            <Button
              type="submit"
              form={EDIT_FORM_ID}
              variant="primary"
              className="grow"
            >
              Update
            </Button>
            <SheetClose asChild>
              <Button variant="secondary" type="button" className="grow">
                Cancel
              </Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
